import { h, Host } from "@stencil/core";
import { debounce } from "../../utils/debounce";
/**
 * @slot (default) - The table column elements defining the table structure
 */
export class TableData {
    constructor() {
        this.fullWidth = false;
        this.data = undefined;
        this.selfSorting = false;
        this.tableColumns = [];
        this.tableColumnsMethods = [];
        this.selectedRows = [];
        this.sortedData = [];
    }
    domNode;
    /** Observer watches for column configuration changes  */
    contentObserver = new MutationObserver(debounce(() => {
        this.updateTableColumns();
    }, 100));
    componentWillLoad() {
        this.updateTableColumns();
        this.setSelectedRows();
    }
    componentDidLoad() {
        this.contentObserver.observe(this.domNode, {
            childList: true,
            subtree: true,
            attributes: true
        });
    }
    disconnectedCallback() {
        this.contentObserver.disconnect();
    }
    async updateTableColumns() {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        const children = Array.from(this.domNode.children);
        this.tableColumns = children.filter(
        // @ts-expect-error Check for typing
        (child) => 
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        typeof child.getColumnMethods === "function");
        this.tableColumnsMethods = await Promise.all(
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        this.tableColumns.map((col) => col.getColumnMethods()));
    }
    getDataWithInternalIds() {
        return this.data.map((row, i) => ({
            ...row,
            _uxTableInternalId: `${i}`
        }));
    }
    /** Emitted when a row was selected */
    rowsSelected;
    /** Emitted when a row was clicked */
    rowClick;
    setSelectedRows() {
        if (this.data === undefined) {
            return;
        }
        this.sortedData = this.getDataWithInternalIds();
        this.selectedRows = this.sortedData
            .filter((row) => {
            return row.selected;
        })
            .map((row) => row._uxTableInternalId);
    }
    emitSelectedRows() {
        this.rowsSelected.emit({
            selectedRows: this.sortedData.filter((row) => this.selectedRows.includes(row._uxTableInternalId))
        });
    }
    /** Handle table sorting */
    sortRows(e) {
        if (!this.selfSorting) {
            return;
        }
        const sorter = e.target;
        const th = sorter.closest("th");
        const { sortedDirection } = sorter;
        // @ts-expect-error Check for typing
        const titleRow = th.parentElement;
        // @ts-expect-error Check for typing
        const colIndex = [].indexOf.call(titleRow.children, th);
        const getSortString = this.tableColumnsMethods[colIndex].getSortString || (() => "");
        if (sortedDirection === "") {
            return (this.sortedData = this.getDataWithInternalIds());
        }
        const comparer = (a, b) => {
            const strA = getSortString(a);
            const strB = getSortString(b);
            return sortedDirection === "asc" ? strA.localeCompare(strB) : strB.localeCompare(strA);
        };
        this.sortedData.sort(comparer);
    }
    render() {
        const { fullWidth, selectedRows, sortedData } = this;
        const baseClass = "ux-table-data";
        return (h(Host, { key: '63dcfcc2c9e25e0edd39fab8030ef7460da5241d', class: {
                [baseClass]: true,
                [`${baseClass}--full-width`]: fullWidth
            } }, h("slot", { key: 'ff7a222244323ea185877c7ced8d9ec1d2f7b42d' }), h("div", { key: 'bb6d475ee93d26adc9978b7ec5ffbcb300dfaaaf', class: `${baseClass}__inner` }, h("table", { key: 'd77341bdb994ff4b9f4709614786f8bdb0ee19bb', class: `${baseClass}__table` }, h("thead", { key: 'f181f075698579a8e88077a996e7d15cab2ba24c', class: `${baseClass}__thead` }, h("tr", { key: '081f778fb5f04310ba8b8f0a34934ac0fb5aa4a5', class: `${baseClass}__tr-head` }, this.tableColumnsMethods?.map((renderer) => renderer.renderHeader(this)))), h("tbody", { key: '7a19cb73c6b9ba7c355e4442ff765c9232154d65', class: `${baseClass}__tbody` }, sortedData?.map((tableRow) => (h("tr", { class: {
                ["ux-table-data__tr"]: true,
                ["ux-table-data__tr--selected"]: selectedRows.includes(tableRow._uxTableInternalId)
            }, onClick: (e) => {
                this.rowClick.emit({
                    row: tableRow,
                    clickEvent: e
                });
            } }, this.tableColumnsMethods?.map((renderer) => renderer.renderCell(tableRow, this))))))))));
    }
    static get is() { return "ux-table-data"; }
    static get originalStyleUrls() {
        return {
            "$": ["table-data.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["table-data.css"]
        };
    }
    static get properties() {
        return {
            "fullWidth": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set to `true` if the table should cover the full width of its containing element"
                },
                "attribute": "full-width",
                "reflect": false,
                "defaultValue": "false"
            },
            "data": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "TableDataRowType[]",
                    "resolved": "TableDataRowType[]",
                    "references": {
                        "TableDataRowType": {
                            "location": "import",
                            "path": "./interface",
                            "id": "src/components/table-data/interface.ts::TableDataRowType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The table data"
                }
            },
            "selfSorting": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If set to `true` the table will handle sorting internally"
                },
                "attribute": "self-sorting",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "tableColumns": {},
            "tableColumnsMethods": {},
            "selectedRows": {},
            "sortedData": {}
        };
    }
    static get events() {
        return [{
                "method": "rowsSelected",
                "name": "uxRowsSelected",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when a row was selected"
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "rowClick",
                "name": "uxRowClick",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when a row was clicked"
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "domNode"; }
    static get watchers() {
        return [{
                "propName": "data",
                "methodName": "setSelectedRows"
            }, {
                "propName": "selectedRows",
                "methodName": "emitSelectedRows"
            }];
    }
    static get listeners() {
        return [{
                "name": "uxTableSort",
                "method": "sortRows",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=table-data.js.map
